import React from 'react';
import { useTheme } from '../../../contexts/ThemeContext';
import { parseSets } from '../lists/MatchScoreCell';

/**
 * A finished match's result, or null while it is still to be played.
 *
 * Reuses the List view's own `parseSets` rather than reading
 * participant_a/b_score directly: those two columns hold SETS WON once a match
 * has more than one set, so they are not a displayable score on their own.
 * Single-set sports show the score itself; multi-set sports show the sets tally,
 * which is what the List view does in the same situation.
 */
const readResult = (match) => {
  if (match?.liveData?.status !== 'completed') return null;
  const sets = parseSets(match.liveData);
  if (sets.length === 0) return null;
  const wonA = sets.filter((s) => s.a > s.b).length;
  const wonB = sets.filter((s) => s.b > s.a).length;
  const a = sets.length === 1 ? sets[0].a : wonA;
  const b = sets.length === 1 ? sets[0].b : wonB;
  return { a, b, winner: a > b ? 'a' : b > a ? 'b' : null };
};

const Slot = ({ entry, placeholder, isFirst, compact, score, isWinner }) => {
  const { theme } = useTheme();
  const isEmpty = !entry && !placeholder;
  const isPlaceholderText = !entry && placeholder;
  const badgeSize = compact ? 'h-4 w-4 text-[9px]' : 'h-5 w-5 text-[10px]';

  return (
    <div
      className={`flex items-center gap-2 ${compact ? 'px-2.5 py-1.5' : 'px-3.5 py-2.5'} ${!isFirst ? `border-t ${theme.border}` : ''}`}
    >
      {entry?.seed ? (
        <span className={`flex shrink-0 items-center justify-center rounded-full font-bold ${badgeSize} ${theme.backgroundSecondary} ${theme.textMuted}`}>
          {entry.seed}
        </span>
      ) : (
        // Reserved but invisible — keeps every name column aligned whether or
        // not that particular slot happens to carry a seed number.
        <span className={`shrink-0 ${badgeSize}`} />
      )}
      <span
        className={`min-w-0 flex-1 truncate ${compact ? 'text-[11px]' : 'text-sm'} ${
          isEmpty ? theme.textMuted : entry?.isPlaceholder || isPlaceholderText ? `italic ${theme.textMuted}` : isWinner ? 'font-bold text-green-400' : `font-semibold ${theme.text}`
        }`}
      >
        {entry?.name || placeholder || 'BYE'}
      </span>
      {score != null && (
        // tabular-nums so the two scores in a card line up under each other
        // however wide the digits are.
        <span
          className={`shrink-0 tabular-nums ${compact ? 'text-[11px]' : 'text-sm'} ${
            isWinner ? 'font-bold text-green-400' : `font-semibold ${theme.textMuted}`
          }`}
        >
          {score}
        </span>
      )}
    </div>
  );
};

const MatchCard = ({ match, compact }) => {
  const { theme } = useTheme();
  // Scores and the winner, once the match has actually been played. A bracket
  // that shows only names makes a finished tournament look identical to one
  // that has not started.
  const result = readResult(match);
  return (
    <div
      // `border-current` + `theme.text` (rather than `theme.border`, a muted
      // shade close to the card's own fill) makes the border pick up
      // whatever this theme's actual text color is — reliably higher-
      // contrast against `theme.card` in every theme, not just the ones
      // where `border` happens to read clearly. Children set their own
      // `text-*` class regardless, so this only affects the border, not any
      // text inside.
      className={`w-full overflow-hidden rounded-xl border border-current ${theme.text} ${theme.card} ${theme.shadow} ${
        match.isBye ? 'opacity-60' : ''
      }`}
    >
      <div
        className={`flex items-center justify-between border-b ${theme.border} ${theme.backgroundSecondary} ${
          compact ? 'px-2.5 py-1' : 'px-3.5 py-1.5'
        }`}
      >
        <span className={`font-bold uppercase tracking-wide ${compact ? 'text-[9px]' : 'text-[10px]'} ${theme.textMuted}`}>
          Match {match.number}
        </span>
      </div>
      <Slot
        entry={match.slotA}
        placeholder={match.placeholderA}
        isFirst
        compact={compact}
        score={result?.a}
        isWinner={result?.winner === 'a'}
      />
      <Slot
        entry={match.slotB}
        placeholder={match.placeholderB}
        compact={compact}
        score={result?.b}
        isWinner={result?.winner === 'b'}
      />
      {match.isBye && !compact && (
        <div className={`border-t ${theme.border} px-3 py-1 text-center text-[10px] font-semibold uppercase tracking-wide ${theme.textMuted}`}>
          Bye — advances automatically
        </div>
      )}
    </div>
  );
};

export default MatchCard;
